import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const RESTORE_KEY = 'shopRestoreState';
const CONTEXT_KEY = 'shopListContext';

export function saveListContext({ searchInput = '', pathname, search }) {
  sessionStorage.setItem(
    CONTEXT_KEY,
    JSON.stringify({ searchInput, pathname, search })
  );
}

export function saveShopScrollState() {
  let searchInput = '';

  try {
    const ctx = JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || '{}');
    const currentPath = `${window.location.pathname}${window.location.search}`;
    const ctxPath = `${ctx.pathname || ''}${ctx.search || ''}`;
    if (ctxPath === currentPath) {
      searchInput = ctx.searchInput || '';
    }
  } catch {
    // Ignore malformed context.
  }

  sessionStorage.setItem(
    RESTORE_KEY,
    JSON.stringify({
      scrollY: window.scrollY,
      pathname: window.location.pathname,
      search: window.location.search,
      searchInput,
    })
  );
}

export function peekShopRestoreState() {
  const raw = sessionStorage.getItem(RESTORE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearShopRestoreState() {
  sessionStorage.removeItem(RESTORE_KEY);
}

export function shouldRestoreScroll(pathname, search) {
  const state = peekShopRestoreState();
  if (!state) return false;

  const targetPath = `${pathname}${search}`;
  const restorePath = `${state.pathname || ''}${state.search || ''}`;
  return targetPath === restorePath;
}

function restoreScrollY(scrollY, attempt = 0) {
  window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });

  if (attempt < 20 && Math.abs(window.scrollY - scrollY) > 5) {
    requestAnimationFrame(() => restoreScrollY(scrollY, attempt + 1));
  }
}

export function useShopScrollRestore({ isReady, onRestore, restoreDeps = [] }) {
  const location = useLocation();
  const [pendingRestore, setPendingRestore] = useState(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  useEffect(() => {
    const state = peekShopRestoreState();
    if (!state) {
      setPendingRestore(null);
      return;
    }

    const targetPath = `${location.pathname}${location.search}`;
    const restorePath = `${state.pathname || ''}${state.search || ''}`;
    if (targetPath !== restorePath) {
      setPendingRestore(null);
      return;
    }

    setPendingRestore(state);
    onRestoreRef.current?.(state);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!pendingRestore || !isReady) return;

    const timer = setTimeout(() => {
      restoreScrollY(pendingRestore.scrollY || 0);
      clearShopRestoreState();
      setPendingRestore(null);
    }, 150);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRestore, isReady, ...restoreDeps]);
}
